import { ChangeEvent, FC, useState } from 'react';
import { Box, Link, Skeleton, Svg, Text, Tooltip } from '@hopskipdrive/react-components';
import OpenInNew from '@mui/icons-material/OpenInNew';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import { Button } from '../../../../../shared/components/Button';
import { Card } from '../../../../../shared/components/Card';
import { CopyToClipboard } from '../../../../../shared/components/CopyToClipboard';
import { Pagination } from '../../../../../shared/components/Pagination';
import { PhotoOrInitials } from '../../../../../shared/components/PhotoOrInitials';
import { colorTokens } from '../../../../../shared/components/variables';
import { formatInTimezone } from '../../../../../shared/utils/helpers/date';
import { estimatedTimeFromNow } from '../../../../../shared/utils/helpers/util';
import { useMessagesAndCallsQuery } from '../../../../hooks/useMessagesAndCallsQuery';
import { MessagesAndCallsListItem } from '../../../../types/messagesAndCalls';
import { ContentHeading } from '../ContentHeading';

import { Container, MessageBodyContainer } from './MessagesAndCalls.styles';

interface EventsProps {
  rideSlug: string;
  timeZone: string;
}

const MESSAGES_CALLS_QUERY_PARAMS = {
  page: 1,
  perPage: 5,
};

export const MessagesAndCalls: FC<EventsProps> = ({ rideSlug, timeZone }) => {
  const [pageNumber, setPageNumber] = useState<number>(MESSAGES_CALLS_QUERY_PARAMS.page);
  const { isFetching, data: messagesAndCallsResponse } = useMessagesAndCallsQuery({
    queryParams: { page: pageNumber, perPage: MESSAGES_CALLS_QUERY_PARAMS.perPage, rideId: rideSlug },
  });

  return (
    <Card>
      <ContentHeading icon="message-contact" headingTitle="Messages and Calls" />
      <ListOfMessagesAndCalls
        messagesOrCalls={messagesAndCallsResponse?.data || []}
        pageCount={
          messagesAndCallsResponse?.meta?.totalCount
            ? Math.ceil(messagesAndCallsResponse.meta?.totalCount / MESSAGES_CALLS_QUERY_PARAMS.perPage)
            : 0
        }
        isFetching={isFetching}
        onPageChange={(_, page) => setPageNumber(page)}
        timeZone={timeZone}
      />
    </Card>
  );
};

interface ListOfEventsProps {
  isFetching: boolean;
  messagesOrCalls: MessagesAndCallsListItem[];
  pageCount: number;
  onPageChange: (event: ChangeEvent<unknown>, page: number) => void;
  timeZone: string;
}

// To facilitate testing, we can export the unconnected component as well
export const ListOfMessagesAndCalls: FC<ListOfEventsProps> = ({
  isFetching,
  messagesOrCalls,
  pageCount,
  onPageChange,
  timeZone,
}) => {
  return (
    <Box display="flex" flexDirection="column" gap="10px">
      {isFetching ? (
        <SkeletonMessagesAndCalls />
      ) : (
        messagesOrCalls?.map((messageOrCall, index) => (
          <Container key={index}>
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="row" gap="6px" alignItems="center" width="100%">
                <PhotoOrInitials
                  key={`sender-message-call-${index}`}
                  fullName={messageOrCall.senderName}
                  pictureUrl={messageOrCall.senderProfilePhotoUrl}
                  size={33}
                  fontSize={16}
                />
                <Text variant="subtitle1" textColor="primaryText">
                  {messageOrCall.senderName === 'System'
                    ? `${messageOrCall.senderName} Bot`
                    : `${messageOrCall.senderType}: ${messageOrCall.senderName}`}
                </Text>
                <Svg icon="time" width="19px" height="19px" style={{ color: colorTokens.text.secondary }} />
                <Text variant="subtitle1" textColor="secondaryText">
                  {formatInTimezone(messageOrCall.createdAt, timeZone, 'EEE, M/dd/yyyy | h:mm aa zzz ')}
                  {estimatedTimeFromNow(messageOrCall.createdAt)}
                </Text>
              </Box>
              <Box display="flex" flexDirection="row" gap="6px" ml="39px" alignItems="center">
                <Text variant="body1" fontWeight={300} textColor="primaryText">
                  {messageOrCall.type === 'sms' ? 'sent SMS to' : 'Called'}
                </Text>
                <Tooltip
                  title={
                    messageOrCall.receiverNumber ? (
                      <Box display="flex" flexDirection="row" gap="6px" alignItems="center" flexWrap="nowrap">
                        <PhoneEnabledIcon style={{ width: '14px', height: '14px' }} />
                        <CopyToClipboard
                          text={messageOrCall.receiverNumber}
                          toastMessage="Phone Number Copied"
                          variant="subtitle2"
                          textStyle={{ whiteSpace: 'nowrap' }}
                        />
                      </Box>
                    ) : (
                      'No Phone Number'
                    )
                  }
                  placement="top"
                >
                  <div>
                    <Link
                      target="_blank"
                      underline="none"
                      href={messageOrCall.receiverUrl}
                      style={{ color: colorTokens.info.main }}
                    >
                      {`${messageOrCall.receiverType}: ${messageOrCall.receiverName}`}
                    </Link>
                  </div>
                </Tooltip>
              </Box>
            </Box>
            <Box ml="39px">
              {messageOrCall.body && (
                <MessageBodyContainer>
                  <Text variant="body1" fontWeight={300} style={{ color: colorTokens.text.primary }}>
                    {messageOrCall.body}
                  </Text>
                </MessageBodyContainer>
              )}
              {messageOrCall.url && (
                <Button
                  color="secondary"
                  variant="text"
                  size="small"
                  startIcon={<OpenInNew width="24px" height="24px" />}
                  onClick={() => window.open(messageOrCall.url, '_blank')}
                  style={{
                    fontWeight: 300,
                    background: colorTokens.surface.light,
                    borderRadius: '12px',
                    padding: '2px 12px',
                    color: colorTokens.info.main,
                    width: '168px',
                  }}
                >
                  Call Recording
                </Button>
              )}
            </Box>
          </Container>
        ))
      )}
      {!isFetching && !messagesOrCalls?.length && (
        <Text variant="body1" fontWeight={300} textColor="secondaryText">
          No messages or calls
        </Text>
      )}
      {pageCount > 1 && <Pagination count={pageCount} siblingCount={3} onChange={onPageChange} />}
    </Box>
  );
};

export const SkeletonMessagesAndCalls = () => (
  <>
    <Container data-testid="messages-and-calls-skeleton">
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexDirection="row" gap="6px" alignItems="center" width="100%">
          <Skeleton variant="circular" width={33} height={33} />
          <Skeleton variant="text" width={161} height={28} />
          <Skeleton variant="circular" width={19} height={19} />
          <Skeleton variant="text" width={359} height={28} />
        </Box>
        <Box display="flex" flexDirection="row" gap="6px" ml="39px" alignItems="center">
          <Skeleton variant="text" width={88} height={24} />
          <Skeleton variant="text" width={181} height={24} />
        </Box>
      </Box>
      <Box ml="39px">
        <MessageBodyContainer>
          <Skeleton variant="text" width={593} height={24} />
          <Skeleton variant="text" width={300} height={24} />
        </MessageBodyContainer>
      </Box>
    </Container>
    <Container data-testid="messages-and-calls-skeleton">
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexDirection="row" gap="6px" alignItems="center" width="100%">
          <Skeleton variant="circular" width={33} height={33} />
          <Skeleton variant="text" width={161} height={28} />
          <Skeleton variant="circular" width={19} height={19} />
          <Skeleton variant="text" width={359} height={28} />
        </Box>
        <Box display="flex" flexDirection="row" gap="6px" ml="39px" alignItems="center">
          <Skeleton variant="text" width={88} height={24} />
          <Skeleton variant="text" width={181} height={24} />
        </Box>
      </Box>
      <Box ml="39px">
        <MessageBodyContainer>
          <Skeleton variant="text" width={593} height={24} />
          <Skeleton variant="text" width={300} height={24} />
        </MessageBodyContainer>
      </Box>
    </Container>
    <Container data-testid="messages-and-calls-skeleton">
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexDirection="row" gap="6px" alignItems="center" width="100%">
          <Skeleton variant="circular" width={33} height={33} />
          <Skeleton variant="text" width={161} height={28} />
          <Skeleton variant="circular" width={19} height={19} />
          <Skeleton variant="text" width={359} height={28} />
        </Box>
        <Box display="flex" flexDirection="row" gap="6px" ml="39px" alignItems="center">
          <Skeleton variant="text" width={88} height={24} />
          <Skeleton variant="text" width={181} height={24} />
        </Box>
      </Box>
      <Box ml="39px">
        <MessageBodyContainer>
          <Skeleton variant="text" width={593} height={24} />
          <Skeleton variant="text" width={300} height={24} />
        </MessageBodyContainer>
      </Box>
    </Container>
  </>
);
